import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Send, Sparkles, RotateCcw, ArrowRight, Wallet, ChevronDown, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { advisorChat, getApp } from "@/lib/api";
import { ADVISOR } from "@/constants/testIds";

const newId = () => "sess-" + Math.random().toString(36).slice(2, 11);

const BUDGET_STORAGE_KEY = "hac_advisor_budget_v1";
const loadBudget = () => {
  try {
    const raw = localStorage.getItem(BUDGET_STORAGE_KEY);
    return raw ? JSON.parse(raw) : { amount_monthly: "", horses: "", users: "", currency: "EUR" };
  } catch {
    return { amount_monthly: "", horses: "", users: "", currency: "EUR" };
  }
};
const saveBudget = (b) => {
  try {
    localStorage.setItem(BUDGET_STORAGE_KEY, JSON.stringify(b));
  } catch { /* quota */ }
};
const budgetPayload = (b) => {
  const out = { currency: b.currency || "EUR" };
  const amt = parseFloat(b.amount_monthly);
  const h = parseInt(b.horses, 10);
  const u = parseInt(b.users, 10);
  if (!isNaN(amt) && amt > 0) out.amount_monthly = amt;
  if (!isNaN(h) && h > 0) out.horses = h;
  if (!isNaN(u) && u > 0) out.users = u;
  // Only include if something meaningful was set
  if (out.amount_monthly === undefined && out.horses === undefined && out.users === undefined) return null;
  return out;
};

const AdvisorPage = () => {
  const { t, lang } = useLang();
  const location = useLocation();
  const [sessionId, setSessionId] = useState(() => newId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [recCache, setRecCache] = useState({});
  const [budget, setBudget] = useState(loadBudget);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const scrollRef = useRef(null);

  const updateBudget = (patch) => {
    setBudget((b) => {
      const next = { ...b, ...patch };
      saveBudget(next);
      return next;
    });
  };
  const clearBudget = () => {
    const empty = { amount_monthly: "", horses: "", users: "", currency: "EUR" };
    setBudget(empty);
    saveBudget(empty);
  };
  const hasActiveBudget = !!budgetPayload(budget);

  useEffect(() => {
    if (location.state?.prefill) setInput(location.state.prefill);
  }, [location.state]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, thinking]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || thinking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: message }]);
    setThinking(true);
    try {
      const res = await advisorChat({ session_id: sessionId, message, language: lang, budget: budgetPayload(budget) });
      setMessages((m) => [
        ...m,
        { role: "assistant", text: res.reply, recommended: res.recommended_slugs || [] },
      ]);
      // Fetch app summaries for recommended slugs (cached)
      for (const s of res.recommended_slugs || []) {
        if (!recCache[s]) {
          try {
            const a = await getApp(s);
            setRecCache((c) => ({ ...c, [s]: a }));
          } catch (e) {
            /* ignore */
          }
        }
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            lang === "nl"
              ? "Er ging iets mis. Probeer het zo opnieuw."
              : "Something went wrong. Please try again.",
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(newId());
    setInput("");
  };

  return (
    <main className="hac-container py-12 md:py-16">
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 flex flex-col h-[75vh] bg-white border border-[#E2E8F0]">
          <header className="px-6 py-5 border-b border-[#E2E8F0]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#2563EB] text-white flex items-center justify-center rounded-full">
                  <Sparkles size={16} />
                </div>
                <div>
                  <div className="font-heading text-xl text-[#0F172A] leading-none">
                    {t.sections.advisor}
                  </div>
                  <div className="text-xs text-[#94A3B8] font-mono">Advisor</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setBudgetOpen((v) => !v)}
                  className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${hasActiveBudget ? "bg-[#EFF6FF] border-[#2563EB] text-[#2563EB]" : "border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A] hover:border-[#94A3B8]"}`}
                  data-testid="advisor-budget-toggle"
                  aria-expanded={budgetOpen}
                >
                  <Wallet size={12} />
                  {lang === "nl" ? "Budget & stal" : "Budget & herd"}
                  {hasActiveBudget && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2563EB]" />}
                  <ChevronDown size={11} className={`transition-transform ${budgetOpen ? "rotate-180" : ""}`} />
                </button>
                <button
                  onClick={reset}
                  data-testid={ADVISOR.newChat}
                  className="text-xs text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1"
                >
                  <RotateCcw size={12} /> {t.advisor.newChat}
                </button>
              </div>
            </div>
            {budgetOpen && (
              <div className="mt-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4" data-testid="advisor-budget-panel">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] text-[#64748B] leading-snug flex-1 pr-3">
                    {lang === "nl"
                      ? "Vul in voor concretere aanbevelingen. De Advisor rekent dan voor jouw aantal paarden/gebruikers de echte maandkosten uit en filtert op budget."
                      : "Fill in for sharper recommendations. The Advisor will compute the real monthly cost for your herd/team size and filter by budget."}
                  </p>
                  {hasActiveBudget && (
                    <button type="button" onClick={clearBudget} className="text-[10px] text-[#64748B] hover:text-red-500 inline-flex items-center gap-1" data-testid="advisor-budget-clear">
                      <X size={10} /> {lang === "nl" ? "Wissen" : "Clear"}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] block mb-1">{lang === "nl" ? "Valuta" : "Currency"}</label>
                    <select
                      value={budget.currency}
                      onChange={(e) => updateBudget({ currency: e.target.value })}
                      className="w-full bg-white border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm"
                      data-testid="advisor-budget-currency"
                    >
                      <option value="EUR">EUR €</option>
                      <option value="USD">USD $</option>
                      <option value="GBP">GBP £</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] block mb-1">{lang === "nl" ? "Max/maand" : "Max/month"}</label>
                    <input
                      type="number" min={0} step="1"
                      value={budget.amount_monthly}
                      onChange={(e) => updateBudget({ amount_monthly: e.target.value })}
                      placeholder="50"
                      className="w-full bg-white border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm"
                      data-testid="advisor-budget-amount"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] block mb-1">{lang === "nl" ? "Aantal paarden" : "Horses"}</label>
                    <input
                      type="number" min={0} step="1"
                      value={budget.horses}
                      onChange={(e) => updateBudget({ horses: e.target.value })}
                      placeholder="12"
                      className="w-full bg-white border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm"
                      data-testid="advisor-budget-horses"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] block mb-1">{lang === "nl" ? "Gebruikers" : "Users"}</label>
                    <input
                      type="number" min={0} step="1"
                      value={budget.users}
                      onChange={(e) => updateBudget({ users: e.target.value })}
                      placeholder="3"
                      className="w-full bg-white border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm"
                      data-testid="advisor-budget-users"
                    />
                  </div>
                </div>
              </div>
            )}
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
            {messages.length === 0 && (
              <div>
                <p className="text-[#64748B] mb-6">{t.sections.advisorSub}</p>
                <div className="grid gap-2">
                  {t.advisor.examples.map((ex, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => send(ex)}
                      data-testid={ADVISOR.example(i)}
                      className="text-left text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 hover:border-[#2563EB] hover:bg-[#EFF6FF] transition-colors"
                    >
                      <span className="text-[#2563EB] mr-2 font-mono text-xs">0{i + 1}</span>
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#2563EB] text-white"
                      : "bg-[#F1F5F9] text-[#0F172A]"
                  }`}
                  data-testid={`advisor-msg-${m.role}-${i}`}
                >
                  {m.text}
                  {m.recommended && m.recommended.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-[#CBD5E1]/40">
                      <div className="overline mb-2 text-[#2563EB]">{t.advisor.recommended}</div>
                      <div className="grid gap-2">
                        {m.recommended.map((slug) => {
                          const a = recCache[slug];
                          return (
                            <Link
                              key={slug}
                              to={`/apps/${slug}`}
                              className="bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 flex items-center justify-between hover:border-[#2563EB] transition-colors"
                              data-testid={`advisor-rec-${slug}`}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-7 h-7 flex items-center justify-center text-white text-xs font-heading rounded-md"
                                  style={{ backgroundColor: a?.color || "#2563EB" }}
                                >
                                  {(a?.name || slug).charAt(0).toUpperCase()}
                                </span>
                                <span className="font-heading text-base text-[#0F172A]">
                                  {a?.name || slug}
                                </span>
                              </div>
                              <ArrowRight size={14} className="text-[#2563EB]" />
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {thinking && (
              <div className="flex justify-start">
                <div className="bg-[#F1F5F9] text-[#64748B] p-4 text-sm italic">
                  {t.advisor.thinking}
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-[#E2E8F0] px-4 py-4 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.advisor.placeholder}
              data-testid={ADVISOR.input}
              disabled={thinking}
              className="hac-input flex-1"
            />
            <button
              type="submit"
              disabled={!input.trim() || thinking}
              data-testid={ADVISOR.send}
              className="hac-btn-primary disabled:opacity-50"
            >
              <Send size={14} /> {t.advisor.send}
            </button>
          </form>
        </div>

        <aside className="space-y-5">
          <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-6">
            <div className="overline mb-3 text-[#2563EB]">
              <Sparkles size={12} className="inline mr-1" /> Advisor
            </div>
            <h2 className="font-heading text-2xl text-[#0F172A] mb-3">
              {t.advisor.title}
            </h2>
            <p className="text-sm text-[#64748B] leading-relaxed">
              {t.sections.advisorSub}
            </p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
            <div className="overline mb-3">{t.nav.compare}</div>
            <p className="text-sm text-[#64748B] mb-4">{t.sections.featuredSub}</p>
            <Link to="/compare" className="hac-btn-secondary w-full justify-center">
              {t.common.compareNow} <ArrowRight size={14} />
            </Link>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default AdvisorPage;
